import { useState } from "react";
import { Box, Link } from "@mui/material";
import { containsHtml, sanitizeTaskDescriptionHtml } from "./htmlUtils";

const CLAMP_LINES = 3;

const descriptionBaseSx = {
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  "& a": {
    color: "primary.main",
    textDecoration: "underline",
  },
  "& p": { margin: 0 },
  "& ul, & ol": { margin: 0, paddingLeft: 2 },
};

const clampedSx = {
  ...descriptionBaseSx,
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: CLAMP_LINES,
  overflow: "hidden",
};

export function DescriptionCell({ text }: { text?: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState(false);
  const content = text ?? "";

  if (!content.trim()) {
    return <>—</>;
  }

  const isHtml = containsHtml(content);
  const contentSx = expanded ? descriptionBaseSx : clampedSx;

  return (
    <Box>
      {isHtml ? (
        <Box
          ref={(el: HTMLDivElement | null) => {
            if (!el || expanded) return;
            const isOverflowing = el.scrollHeight > el.clientHeight + 1;
            setOverflow((prev) => (prev === isOverflowing ? prev : isOverflowing));
          }}
          sx={contentSx}
          dangerouslySetInnerHTML={{ __html: sanitizeTaskDescriptionHtml(content) }}
        />
      ) : (
        <Box
          ref={(el: HTMLDivElement | null) => {
            if (!el || expanded) return;
            const isOverflowing = el.scrollHeight > el.clientHeight + 1;
            setOverflow((prev) => (prev === isOverflowing ? prev : isOverflowing));
          }}
          sx={contentSx}
        >
          {content}
        </Box>
      )}
      {overflow && (
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{ mt: 0.5, display: "inline-block" }}
        >
          {expanded ? "Show less" : "Show more"}
        </Link>
      )}
    </Box>
  );
}
